import Question from "@/modules/RegistrationSurvey/ts/Question";

const CONSOLE_DEBUG = false;

export default class SurveyEngine {

    private questions: Array<Question>;

    private questionIndex: number;
    private disqualifiedQuestionIds: Array<number>;

    public constructor(questions: Array<Question>) {
        this.questions = questions;
        this.questionIndex = -1;
        this.disqualifiedQuestionIds = [];
    }

    public getNextQuestion(): Question | null {

        this.questionIndex++;

        if (this.questionsRemaining() === 0) return null;

        for (let index = this.questionIndex; index < this.questions.length; index++) {

            const q = this.questions[index];

            if (this.isConditionallyHidden(index)) {

                if (CONSOLE_DEBUG) console.log("---> " + q.data_variable.name + " conditionally hidden");

                if (this.isLastQuestion(index)) {

                    if (CONSOLE_DEBUG) {
                        console.log("---> " + q.data_variable.name + " Last question detected");
                    }

                    this.questionIndex = index;
                    return null;
                }

            } else if (q.hide_question) {

                if (this.isLastQuestion(index)) {

                    if (CONSOLE_DEBUG) {
                        console.log("---> " + q.data_variable.name + " hidden (Last question)");
                    }

                    this.questionIndex = index;
                    q.setProcessed();
                    return null;
                }

                if (CONSOLE_DEBUG) {
                    console.log("---> " + q.data_variable.name + " hidden");
                }

                q.setProcessed();

            } else {

                if (CONSOLE_DEBUG) {
                    console.log("---> " + q.data_variable.name + " visible");
                }

                this.questionIndex = index;
                break;
            }
        }

        this.debug();

        return new Question(this.questions[this.questionIndex]);
    }

    public getPreviousQuestion(): Question | null {

        if (this.questionIndex === 0) throw new Error("No previous questions!");

        const backedFromQuestion = this.questions[this.questionIndex];
        backedFromQuestion.reset();

        this.questionIndex--;

        for (let index = this.questionIndex; index >= 0; index--) {

            const question = this.questions[index];

            if (this.isConditionallyHidden(index)) {
                question.reset();
                continue;
            }

            question.setBacked();
            this.questionIndex = index;

            break;
        }

        this.debug();

        return this.questions[this.questionIndex];
    }

    private isLastQuestion(index: number) {
        return index === this.questions.length - 1;
    }

    public saveAnswer(answer: any) {

        let question = this.getCurrentQuestion();

        if (CONSOLE_DEBUG) console.log("---> Answer set for " + question.getName())

        this.recordDisqualifyingQuestion(question, answer);

        question.setAnswer(answer);
    }

    public skipQuestion() {

        const question = this.getCurrentQuestion();

        if (!question.isAnswerRequired()) {
            question.setUserSkipped(true);
        }
    }

    private getCurrentQuestion(): Question {

        if (!this.questions[this.questionIndex]) {
            throw new Error(
                "Current question with index " + this.questionIndex +
                " not found when calling saveAnswer() in SurveyEngine.ts"
            );
        }
        return this.questions[this.questionIndex];
    }

    public isComplete(): boolean {
        const unansweredCount = this.questions.filter((question: Question) => !question.isProcessed());
        return unansweredCount.length === 0;
    }

    public questionsRemaining(): number {
        return this.questions.length - this.questionIndex;
    }

    public getProgressPercent(): number {
        return Math.round((this.getAnsweredCount() / this.questions.length) * 100);
    }

    public isFirstQuestion(): boolean {
        return this.questionIndex === 0;
    }

    public getAnswers(): Object {
        let answers = {};

        this.questions.forEach(question => {
            answers[question.data_variable.name] = question.getAnswer();
        });

        return answers;
    }

    public isDisqualified(): boolean {
        return this.disqualifiedQuestionIds.length > 0;
    }

    public addToDisqualifiedQuestions(question: Question): void {

        if (this.disqualifiedQuestionIds.includes(question.id)) {
            return;
        }

        this.disqualifiedQuestionIds.push(question.id);
    }

    private getAnsweredCount() {
        return this.questions.length - this.getUnansweredCount();
    }

    private getUnansweredCount() {
        const unanswered = this.questions.filter((question: Question) => !question.isProcessed());
        return unanswered.length;
    }

    private isConditionallyHidden(index: number): boolean {

        const question = this.questions[index];

        if (!question.condition || !question.condition_data_variable_id) return false;

        const targetQuestion = this.findTargetQuestion(question.condition_data_variable_id);

        if (!['checkbox', 'radio'].includes(targetQuestion.data_variable.type)) {
            throw new Error("Condition do not support data variable type " + targetQuestion.data_variable.type);
        }

        if (targetQuestion.data_variable.type === 'checkbox') {
            if (!this.isCheckboxConditionTrue(targetQuestion, question)) {
                question.setConditionallyHidden(true);
                question.setAnswer(null);
                return true;
            }
        }

        if (targetQuestion.data_variable.type === 'radio') {
            if (!this.isRadioConditionTrue(targetQuestion, question)) {
                question.setConditionallyHidden(true);
                question.setAnswer(null);
                return true;
            }
        }
        return false;
    }

    private findTargetQuestion(dataVariableId: number): Question {

        let targetQuestion = this.questions.find(q => q.data_variable.id === dataVariableId);
        if (!targetQuestion) throw new Error('Target question not found!');
        return targetQuestion;
    }

    private isCheckboxConditionTrue(targetQuestion: Question, currentQuestion: Question): boolean {

        if (!Array.isArray(targetQuestion.getAnswer())) return false;
        if (!currentQuestion.condition) throw new Error("Current question contains no condition!");

        // @ts-ignore
        const matches = targetQuestion.getAnswer().filter(optionId => currentQuestion.condition.value.includes(optionId));
        const result = matches.length > 0;

        const booleanText = result ? 'TRUE' : 'FALSE';
        if (CONSOLE_DEBUG) console.info("---> Checkbox condition (" + currentQuestion.data_variable.name + ") " + booleanText);

        return result;
    }

    private isRadioConditionTrue(targetQuestion: Question, currentQuestion: Question): boolean {

        if (targetQuestion.getAnswer() === null) return false;

        // @ts-ignore
        const result = currentQuestion.condition.value.includes(targetQuestion.getAnswer());

        const booleanText = result ? 'TRUE' : 'FALSE';

        if (CONSOLE_DEBUG) console.info("---> Radio question (" + currentQuestion.data_variable.name + ") " + booleanText);

        return result;
    }

    private recordDisqualifyingQuestion(question: Question, answer: any) {

        if (question.disqualify_options === null) {
            return;
        }

        if (question.data_variable.type === 'checkbox') {

            if (!Array.isArray(answer)) {
                console.error("Checkbox answer is not an array in SurveyEngine.ts!");
                return;
            }

            answer.forEach((optionId: number) => {

                if (question.disqualify_options.includes(optionId)) {

                    if (CONSOLE_DEBUG) {
                        console.info("---> Disqualified " + question.getName());
                    }

                    this.addToDisqualifiedQuestions(question);
                }
            })
        }

        if (question.data_variable.type === 'radio' && question.disqualify_options.includes(answer)) {

            if (CONSOLE_DEBUG) {
                console.info("---> Disqualified " + question.getName());
            }

            this.addToDisqualifiedQuestions(question);

            return false;
        }
    }

    private debug() {

        if (!CONSOLE_DEBUG) return;

        console.log("".padStart(50, "-"));
        console.log(" SURVEY ENGINE");
        console.log("".padStart(50, "-"));
        console.log("Disqualified: " + (this.isDisqualified() ? 'YES' : 'NO'));
        console.log("Questions remaining: " + this.questionsRemaining());
        console.log("Questions answered: " + this.getAnsweredCount());
        console.log("Questions unanswered: " + this.getUnansweredCount());
        console.log("Questions total: " + this.questions.length);
        console.log("Progress: " + this.getProgressPercent() + "%");
        console.log("Is complete: " + this.isComplete());

        console.log("".padStart(50, "-"));
        console.log(" Questions and answers");
        console.log("".padStart(50, "-"));

        this.questions.forEach((question, index) => {

            const pointer = index === this.questionIndex ? ' <----- POINTER' : '';

            let parts = [];

            parts.push(index + "".padStart(2));
            parts.push(question.data_variable.name.padEnd(20));

            const reason = this.getReason(question);
            parts.push(reason.padEnd(20));

            const answer = this.getAnswer(question);
            parts.push(answer.padEnd(20));

            const isAnswered = question.isProcessed();

            const answered = isAnswered ? 'Processed' : '';
            parts.push(answered.padEnd(20));
            parts.push(pointer);

            console.log(parts.join(" "));
        })
    }

    private getAnswer(question: Question) {

        let answer;

        if (question.getAnswer()) {
            answer = "Answer: " + question.getAnswer();
        } else {
            answer = "Answer: NULL";
        }
        return answer;
    }

    private getReason(question: Question) {

        let reason;

        if (question.conditionally_hidden) {
            reason = "(COND SKIPPED)";
        } else if (question.user_skipped) {
            reason = "(USER SKIPPED)";
        } else {
            reason = "";
        }
        return reason;
    }
}
